import { getParameterByName } from './general';

import { isStandaloneMode, usercentricsSettingsId } from '../config';
import { Locale } from '../models/locale.model';

interface UsercentricsCmp {
    changeLanguage: (language: string) => Promise<void>;
}

const SCRIPT_ID = 'usercentrics-cmp';
const LANGUAGES: Locale[] = ['de', 'en', 'it', 'fr'];

// Last language the app asked for. Re-applied once the CMP is ready, so a switch during its start-up isn't lost.
let requestedLanguage: Locale | undefined;

export const isLocale = (value: unknown): value is Locale => LANGUAGES.includes(value as Locale);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getCmp = (): UsercentricsCmp | undefined => (window as any).__ucCmp as UsercentricsCmp | undefined;

const applyRequestedLanguage = () => {
    const cmp = getCmp();

    if (!cmp || !requestedLanguage) {
        return;
    }

    cmp.changeLanguage(requestedLanguage).catch(error => console.warn('Usercentrics: changing language failed', error));
};

// In standalone mode the UI language comes from the `?lang=` query param rather than window.django.
const resolveLanguage = (fallback: Locale): Locale => {
    const langParam = getParameterByName('lang');

    return isLocale(langParam) ? langParam : fallback;
};

// Must run before GTM is mounted so Consent Mode defaults are in place before any tag fires.
// In django mode the host app is responsible for the CMP, same as for GTM.
export const loadUsercentrics = (fallbackLanguage: Locale) => {
    if (!isStandaloneMode || !usercentricsSettingsId || document.getElementById(SCRIPT_ID)) {
        return;
    }

    requestedLanguage = resolveLanguage(fallbackLanguage);
    // The CMP may fall back to <html lang> when it re-renders, so keep it in sync with the requested language.
    document.documentElement.lang = requestedLanguage;
    window.addEventListener('UC_UI_INITIALIZED', applyRequestedLanguage);

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://web.cmp.usercentrics.eu/ui/loader.js';
    script.async = true;
    script.dataset.settingsId = usercentricsSettingsId;
    script.dataset.language = requestedLanguage;
    document.head.appendChild(script);
};

// Idempotent, so it can be called on every navigation independently of the intl state.
export const changeUsercentricsLanguage = (language: Locale) => {
    const script = document.getElementById(SCRIPT_ID);

    if (!script || language === requestedLanguage) {
        return;
    }

    requestedLanguage = language;
    document.documentElement.lang = language;
    // Only read by the loader on start-up, kept in sync so a reload/late loader and DevTools reflect the current language.
    script.dataset.language = language;
    applyRequestedLanguage();
};
